import { useEffect, useMemo, useState } from "react";
import { HelpdeskBrandingAPI } from "../../api/helpdeskBranding";
import { TicketsAPI } from "../../api/tickets";

const EMPTY_PROFILE = {
  id: null,
  name: "",
  organisation_name: "",
  primary_color: "#1d4ed8",
  secondary_color: "#f3f4f6",
  logo_url: "",
  footer_text: "",
  is_default: false,
};

function getAssignedProfileLabel(schoolId, profiles, assignmentMap) {
  const assignedProfileId = Object.keys(assignmentMap || {}).find((profileId) =>
    (assignmentMap[profileId] || []).includes(schoolId)
  );

  if (!assignedProfileId) {
    const defaultProfile = profiles.find((p) => p.is_default);
    return defaultProfile ? `${defaultProfile.name} (default)` : "Default profile";
  }

  const profile = profiles.find(
    (p) => String(p.id) === String(assignedProfileId)
  );

  return profile ? profile.name : "Assigned profile";
}

export default function HelpdeskBrandingPage() {
  const [profiles, setProfiles] = useState([]);
  const [schools, setSchools] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [form, setForm] = useState(EMPTY_PROFILE);
  const [assignedSchoolIds, setAssignedSchoolIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingSchools, setSavingSchools] = useState(false);
  const [message, setMessage] = useState("");
  const [assignmentMap, setAssignmentMap] = useState({});

  const selectedProfile = useMemo(
    () =>
      profiles.find((p) => String(p.id) === String(selectedProfileId)) || null,
    [profiles, selectedProfileId]
  );

  async function loadProfiles() {
    const rows = await HelpdeskBrandingAPI.listProfiles();
    setProfiles(Array.isArray(rows) ? rows : []);
    return Array.isArray(rows) ? rows : [];
  }

  async function loadSchools() {
    const schoolPerms = await TicketsAPI.allowedSchools("tickets.edit");
    setSchools(Array.isArray(schoolPerms) ? schoolPerms : []);
    return Array.isArray(schoolPerms) ? schoolPerms : [];
  }

  async function loadResolvedAssignments(profileRows, schoolRows) {
    const nextAssignmentMap = {};

    await Promise.all(
      schoolRows.map(async (school) => {
        try {
          const resolved = await HelpdeskBrandingAPI.getResolvedProfile(school.id);
          if (resolved?.id) {
            if (!nextAssignmentMap[resolved.id]) nextAssignmentMap[resolved.id] = [];
            nextAssignmentMap[resolved.id].push(school.id);
          }
        } catch {
          // ignore per-school lookup failure
        }
      })
    );

    setAssignmentMap(nextAssignmentMap);

    const defaultProfile =
      profileRows.find((p) => p.is_default) || profileRows[0] || null;

    if (defaultProfile) {
      setSelectedProfileId(String(defaultProfile.id));
      setForm({
        id: defaultProfile.id,
        name: defaultProfile.name || "",
        organisation_name: defaultProfile.organisation_name || "",
        primary_color: defaultProfile.primary_color || "#1d4ed8",
        secondary_color: defaultProfile.secondary_color || "#f3f4f6",
        logo_url: defaultProfile.logo_url || "",
        footer_text: defaultProfile.footer_text || "",
        is_default: !!defaultProfile.is_default,
      });
      setAssignedSchoolIds(nextAssignmentMap[defaultProfile.id] || []);
    }

    return nextAssignmentMap;
  }

  useEffect(() => {
    async function load() {
      try {
        const [profileRows, schoolRows] = await Promise.all([
          loadProfiles(),
          loadSchools(),
        ]);

        await loadResolvedAssignments(profileRows, schoolRows);
      } catch (err) {
        setMessage(err.message || "Failed to load branding");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  const handleSelectProfile = (profileId) => {
    setSelectedProfileId(profileId);

    if (profileId === "new") {
      setForm(EMPTY_PROFILE);
      setAssignedSchoolIds([]);
      return;
    }

    const profile =
      profiles.find((p) => String(p.id) === String(profileId)) || null;

    if (!profile) return;

    setForm({
      id: profile.id,
      name: profile.name || "",
      organisation_name: profile.organisation_name || "",
      primary_color: profile.primary_color || "#1d4ed8",
      secondary_color: profile.secondary_color || "#f3f4f6",
      logo_url: profile.logo_url || "",
      footer_text: profile.footer_text || "",
      is_default: !!profile.is_default,
    });

    setAssignedSchoolIds(assignmentMap[profile.id] || []);
    setMessage("");
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      setMessage("");

      const saved = await HelpdeskBrandingAPI.saveProfile({
        id: form.id || undefined,
        name: form.name,
        organisation_name: form.organisation_name,
        primary_color: form.primary_color,
        secondary_color: form.secondary_color,
        logo_url: form.logo_url || null,
        footer_text: form.footer_text,
        is_default: !!form.is_default,
      });

      const freshProfiles = await loadProfiles();
      const savedId = saved?.id || form.id;

      setSelectedProfileId(String(savedId));
      const freshProfile =
        freshProfiles.find((p) => String(p.id) === String(savedId)) || saved;

      setForm({
        id: freshProfile.id,
        name: freshProfile.name || "",
        organisation_name: freshProfile.organisation_name || "",
        primary_color: freshProfile.primary_color || "#1d4ed8",
        secondary_color: freshProfile.secondary_color || "#f3f4f6",
        logo_url: freshProfile.logo_url || "",
        footer_text: freshProfile.footer_text || "",
        is_default: !!freshProfile.is_default,
      });

      setMessage("Brand profile saved");
    } catch (err) {
      setMessage(err.message || "Failed to save brand profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveSchools = async () => {
    if (!form.id) {
      setMessage("Save the brand profile first");
      return;
    }

    try {
      setSavingSchools(true);
      setMessage("");

      await HelpdeskBrandingAPI.assignSchools(form.id, assignedSchoolIds);

      const nextAssignmentMap = { ...assignmentMap };

      Object.keys(nextAssignmentMap).forEach((profileId) => {
        nextAssignmentMap[profileId] = (nextAssignmentMap[profileId] || []).filter(
          (schoolId) => !assignedSchoolIds.includes(schoolId)
        );
      });

      nextAssignmentMap[form.id] = assignedSchoolIds;
      setAssignmentMap(nextAssignmentMap);

      setMessage("School assignments saved");
    } catch (err) {
      setMessage(err.message || "Failed to save school assignments");
    } finally {
      setSavingSchools(false);
    }
  };

  if (loading) return <p>Loading branding…</p>;

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <h1 className="text-2xl font-semibold">Helpdesk Brand Profiles</h1>

      {message ? (
        <div className="border rounded px-3 py-2 text-sm bg-white">
          {message}
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        <div className="border rounded p-4 bg-white space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Profile</label>
            <select
              className="border p-2 w-full"
              value={selectedProfileId}
              onChange={(e) => handleSelectProfile(e.target.value)}
            >
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.is_default ? " (Default)" : ""}
                </option>
              ))}
              <option value="new">+ New brand profile</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Profile name</label>
            <input
              className="border p-2 w-full"
              value={form.name}
              onChange={(e) =>
                setForm((f) => ({ ...f, name: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Organisation name
            </label>
            <input
              className="border p-2 w-full"
              value={form.organisation_name}
              onChange={(e) =>
                setForm((f) => ({ ...f, organisation_name: e.target.value }))
              }
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="is_default"
              type="checkbox"
              checked={!!form.is_default}
              onChange={(e) =>
                setForm((f) => ({ ...f, is_default: e.target.checked }))
              }
            />
            <label htmlFor="is_default" className="text-sm font-medium">
              Default profile
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Primary colour</label>
            <input
              type="color"
              className="border p-1 h-10 w-20"
              value={form.primary_color}
              onChange={(e) =>
                setForm((f) => ({ ...f, primary_color: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Secondary colour</label>
            <input
              type="color"
              className="border p-1 h-10 w-20"
              value={form.secondary_color}
              onChange={(e) =>
                setForm((f) => ({ ...f, secondary_color: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Logo URL</label>
            <input
              className="border p-2 w-full"
              value={form.logo_url}
              onChange={(e) =>
                setForm((f) => ({ ...f, logo_url: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Footer text</label>
            <textarea
              className="border p-2 w-full"
              rows={3}
              value={form.footer_text}
              onChange={(e) =>
                setForm((f) => ({ ...f, footer_text: e.target.value }))
              }
            />
          </div>

          <button
            className="bg-blue-600 text-white px-4 py-2 rounded"
            onClick={handleSaveProfile}
            disabled={savingProfile}
          >
            {savingProfile ? "Saving..." : "Save profile"}
          </button>
        </div>

        <div className="space-y-6">
          <div className="border rounded p-4 bg-white space-y-4">
            <h2 className="text-lg font-semibold">Assigned schools</h2>

            <div className="border rounded divide-y max-h-[320px] overflow-y-auto">
              {schools.map((s) => {
                const checked = assignedSchoolIds.includes(s.id);
                const assignedLabel = getAssignedProfileLabel(
                  s.id,
                  profiles,
                  assignmentMap
                );

                return (
                  <label
                    key={s.id}
                    className="flex items-start justify-between gap-4 p-3 cursor-pointer hover:bg-slate-50"
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAssignedSchoolIds((prev) =>
                              prev.includes(s.id) ? prev : [...prev, s.id]
                            );
                          } else {
                            setAssignedSchoolIds((prev) =>
                              prev.filter((id) => id !== s.id)
                            );
                          }
                        }}
                      />

                      <div>
                        <div className="font-medium">{s.name}</div>
                        <div className="text-xs text-slate-500">
                          Currently: {assignedLabel}
                        </div>
                      </div>
                    </div>

                    {checked ? (
                      <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                        Selected here
                      </span>
                    ) : null}
                  </label>
                );
              })}
            </div>

            <button
              className="bg-slate-700 text-white px-4 py-2 rounded"
              onClick={handleSaveSchools}
              disabled={savingSchools || !form.id}
            >
              {savingSchools ? "Saving..." : "Save school assignments"}
            </button>
          </div>

          <div className="border rounded overflow-hidden bg-white">
            <div
              className="px-6 py-5 text-white"
              style={{ backgroundColor: form.primary_color || "#1d4ed8" }}
            >
              {form.logo_url ? (
                <img
                  src={form.logo_url}
                  alt="Logo"
                  className="h-10 mb-3 max-w-[180px] object-contain bg-white p-1 rounded"
                />
              ) : null}

              <div className="text-xl font-semibold">
                {form.organisation_name || "Nightjar Helpdesk"}
              </div>
              <div className="text-sm opacity-90 mt-1">Helpdesk update</div>
            </div>

            <div className="p-6 bg-white">
              <div className="text-xl font-semibold mb-2">Example Ticket Title</div>
              <div className="text-sm text-gray-500 mb-5">Ticket #123</div>

              <div className="text-sm leading-6">
                <p>This is a preview of your helpdesk email layout.</p>
                <p className="mt-3">
                  The actual helpdesk notification content will appear here.
                </p>
              </div>
            </div>

            <div
              className="px-6 py-4 text-xs text-gray-600 border-t"
              style={{ backgroundColor: form.secondary_color || "#f3f4f6" }}
            >
              {form.footer_text || "Sent by Nightjar Helpdesk"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}